import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import { api, type Me } from "./api";
import { MascotHead } from "./components/Mascot";
import { Loading, NotFound } from "./components/ui";
import { Account } from "./pages/Account";
import { CustomerTicket, CustomerTickets, NewTicket } from "./pages/customer";
import { Invoices } from "./pages/Invoices";
import { Login } from "./pages/Login";
import { StaffCustomer, StaffCustomers } from "./pages/staff/Customers";
import { StaffDashboard } from "./pages/staff/Dashboard";
import { StaffItem } from "./pages/staff/Item";
import { StaffNewItem } from "./pages/staff/NewItem";
import { StaffProjects } from "./pages/staff/Projects";
import { StaffQueue } from "./pages/staff/Queue";
import { StaffSettings } from "./pages/staff/Settings";
import { TourProvider } from "./tour/Tour";

const MeContext = createContext<{ me: Me; refresh: () => void } | null>(null);
export function useMe() {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe outside a signed-in shell");
  return ctx;
}

export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const refresh = () =>
    api<{ user: Me | null }>("/auth/me")
      .then((r) => setMe(r.user))
      .catch(() => setMe(null));
  useEffect(() => {
    refresh();
    const onOut = () => setMe(null);
    window.addEventListener("dl:signed-out", onOut);
    return () => window.removeEventListener("dl:signed-out", onOut);
  }, []);

  if (me === undefined) return <div className="page"><Loading /></div>;
  if (!me) return <Login onSignedIn={refresh} />;

  return (
    <MeContext.Provider value={{ me, refresh }}>
      <TourProvider me={me} onSeen={refresh}>
        <Shell me={me}>{me.role === "staff" ? <StaffRoutes /> : <CustomerRoutes />}</Shell>
      </TourProvider>
    </MeContext.Provider>
  );
}

function CustomerRoutes() {
  return (
    <Switch>
      <Route path="/">
        <Redirect to="/tickets" />
      </Route>
      <Route path="/tickets" component={CustomerTickets} />
      <Route path="/tickets/new" component={NewTicket} />
      <Route path="/tickets/:number">{(p) => <CustomerTicket number={p.number} />}</Route>
      <Route path="/invoices" component={Invoices} />
      <Route path="/account" component={Account} />
      <Route component={NotFound} />
    </Switch>
  );
}

function StaffRoutes() {
  return (
    <Switch>
      <Route path="/">
        <Redirect to="/staff" />
      </Route>
      <Route path="/staff" component={StaffDashboard} />
      <Route path="/staff/queue" component={StaffQueue} />
      <Route path="/staff/new" component={StaffNewItem} />
      <Route path="/staff/items/:number">{(p) => <StaffItem number={p.number} />}</Route>
      <Route path="/staff/customers" component={StaffCustomers} />
      <Route path="/staff/customers/:id">{(p) => <StaffCustomer id={p.id} />}</Route>
      <Route path="/staff/projects" component={StaffProjects} />
      <Route path="/staff/invoices" component={Invoices} />
      <Route path="/staff/settings" component={StaffSettings} />
      <Route path="/account" component={Account} />
      {/* Links in customer emails use /tickets/N; staff land on the staff view. */}
      <Route path="/tickets/:number">{(p) => <Redirect to={`/staff/items/${p.number}`} />}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function NavLink({ href, children, exact = false }: { href: string; children: ReactNode; exact?: boolean }) {
  // Tour target: nav-dashboard, nav-queue, nav-tickets, … (from the label).
  const tour = `nav-${String(children).toLowerCase()}`;
  const [loc] = useLocation();
  const active = exact ? loc === href : loc === href || loc.startsWith(`${href}/`);
  return (
    <Link href={href} className={active ? "active" : undefined} aria-current={active ? "page" : undefined} data-tour={tour}>
      {children}
    </Link>
  );
}

function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const signOut = async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => null);
    window.location.href = "/";
  };
  return (
    <>
      <a href="#main" className="sr-only">
        Skip to content
      </a>
      <header className="header">
        <div className="header-inner">
          <Link href="/" className="brand" aria-label="Departure Labs Support home">
            <MascotHead />
            <span>
              <span className="brand-name">Departure Labs</span>
              <br />
              <span className="brand-tag">SUPPORT</span>
            </span>
          </Link>
          <nav className="nav" aria-label="Main">
            {me.role === "staff" ? (
              <>
                <NavLink href="/staff" exact>Dashboard</NavLink>
                <NavLink href="/staff/queue">Queue</NavLink>
                <NavLink href="/staff/customers">Customers</NavLink>
                <NavLink href="/staff/projects">Projects</NavLink>
                <NavLink href="/staff/invoices">Invoices</NavLink>
                <NavLink href="/staff/settings">Settings</NavLink>
              </>
            ) : (
              <>
                <NavLink href="/tickets">Tickets</NavLink>
                <NavLink href="/invoices">Invoices</NavLink>
              </>
            )}
          </nav>
          <div className="header-user">
            <Link href="/account" style={{ color: "inherit" }} data-tour="nav-account">
              {me.name || me.email}
            </Link>
            <button className="btn btn-small" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main id="main" tabIndex={-1} style={{ outline: "none" }}>
        {children}
      </main>
    </>
  );
}
